import React from 'react';
import { ClipboardList, ArrowRight } from 'lucide-react';
import { ItemOverviewOpenTasksDto } from '../../../../../types/itemOverview';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { StatusBadge } from '../../../../common/StatusBadge';
import { formatDate, formatQty } from '../format';
import { SectionCard } from './SectionCard';

interface ItemOpenTasksSectionProps {
  tasks: ItemOverviewOpenTasksDto;
}

// StatusCodes-typer (se StatusCodes_Oversikt.md) — visningsnavn hentes via StatusBadge
const ST_LINE_STATUS = 1;
const ST_INT_RELOCATION_TRANS_TYPE = 25;

const Row: React.FC<{ badge: React.ReactNode; main: React.ReactNode; sub?: React.ReactNode; qty: number }> = ({ badge, main, sub, qty }) => (
  <li className="flex items-center gap-3 py-2.5">
    <div className="flex-shrink-0">{badge}</div>
    <div className="flex-1 min-w-0">
      <div className="text-sm text-gray-900 font-mono break-words">{main}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5 break-words">{sub}</div>}
    </div>
    <div className="flex-shrink-0 text-sm font-semibold text-gray-900">{formatQty(qty)}</div>
  </li>
);

const FromTo: React.FC<{ from?: string | null; to?: string | null }> = ({ from, to }) => (
  <span className="inline-flex items-center gap-1 flex-wrap">
    {from || '-'}
    <ArrowRight className="h-3 w-3 text-gray-400" />
    {to || '-'}
  </span>
);

/**
 * Ventende operasjoner på varen: plukk (PListLine), utlegg/påfylling (StcIntRelOrderLine),
 * mottatt-venter-på-utlegg (StcIntRelocation) og telling/korreksjon/flytt (StcTrans).
 */
export const ItemOpenTasksSection: React.FC<ItemOpenTasksSectionProps> = ({ tasks }) => {
  const { t } = useTranslation();
  const total = tasks.picking.length + tasks.putAwayReplenishment.length
    + tasks.awaitingPutAway.length + tasks.pendingTransactions.length;

  const groupTitle = (text: string) => (
    <h3 className="text-xs font-medium text-gray-500 uppercase mt-3 first:mt-0">{text}</h3>
  );

  return (
    <SectionCard
      icon={<ClipboardList className="h-4 w-4" />}
      title={t('warehouse.modules.itemCard.tasks.title', {}, 'Åpne oppgaver')}
      count={total}
    >
      {total === 0 ? (
        <p className="text-sm text-gray-500 py-2">{t('warehouse.modules.itemCard.tasks.empty', {}, 'Ingen åpne oppgaver på varen')}</p>
      ) : (
        <div>
          {tasks.picking.length > 0 && (
            <>
              {groupTitle(t('warehouse.modules.itemCard.tasks.picking', {}, 'Plukk'))}
              <ul className="divide-y divide-gray-100">
                {tasks.picking.map(p => (
                  <Row
                    key={`pl-${p.pListID}-${p.pListLineNo}`}
                    badge={<span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">PL {p.pListID}</span>}
                    main={p.binCode || '-'}
                    sub={[p.orderNoERP || p.orderNo, p.operatorID, p.deliveryDate ? formatDate(p.deliveryDate) : null].filter(Boolean).join(' · ')}
                    qty={p.qtyRemaining}
                  />
                ))}
              </ul>
            </>
          )}

          {tasks.putAwayReplenishment.length > 0 && (
            <>
              {groupTitle(t('warehouse.modules.itemCard.tasks.putAwayReplenishment', {}, 'Utlegg og påfylling'))}
              <ul className="divide-y divide-gray-100">
                {tasks.putAwayReplenishment.map(r => (
                  <Row
                    key={`rel-${r.stcOrderNo}-${r.stcLineNo}`}
                    badge={r.transType != null
                      ? <StatusBadge value={r.transType} statusType={ST_INT_RELOCATION_TRANS_TYPE} />
                      : <span className="text-xs text-gray-400">-</span>}
                    main={<FromTo from={r.fromBinCode} to={r.toBinCode} />}
                    sub={[`#${r.stcOrderNo}`, r.operatorID, r.batchNo || r.serialNo].filter(Boolean).join(' · ')}
                    qty={r.quantity - r.quantityMoved}
                  />
                ))}
              </ul>
            </>
          )}

          {tasks.awaitingPutAway.length > 0 && (
            <>
              {groupTitle(t('warehouse.modules.itemCard.tasks.awaitingPutAway', {}, 'Mottatt — venter på lokasjon'))}
              <ul className="divide-y divide-gray-100">
                {tasks.awaitingPutAway.map(r => (
                  <Row
                    key={`await-${r.relID}`}
                    badge={r.transType != null
                      ? <StatusBadge value={r.transType} statusType={ST_INT_RELOCATION_TRANS_TYPE} />
                      : <span className="text-xs text-gray-400">-</span>}
                    main={r.binCode || '-'}
                    sub={[formatDate(r.transDt), r.batchNo || r.serialNo].filter(s => s && s !== '-').join(' · ')}
                    qty={r.quantity}
                  />
                ))}
              </ul>
            </>
          )}

          {tasks.pendingTransactions.length > 0 && (
            <>
              {groupTitle(t('warehouse.modules.itemCard.tasks.pendingTransactions', {}, 'Telling, korreksjon og flytt'))}
              <ul className="divide-y divide-gray-100">
                {tasks.pendingTransactions.map(s => (
                  <Row
                    key={`st-${s.batchID}-${s.lineID}`}
                    badge={s.lineType != null
                      ? <StatusBadge value={s.lineType} statusType={3} />
                      : <span className="text-xs text-gray-400">-</span>}
                    main={s.toLocationName ? <FromTo from={s.locationName} to={s.toLocationName} /> : (s.locationName || '-')}
                    sub={<span className="inline-flex items-center gap-2 flex-wrap">
                      {s.lineStatus != null && <StatusBadge value={s.lineStatus} statusType={ST_LINE_STATUS} />}
                      {[s.operatorID, formatDate(s.transDt)].filter(v => v && v !== '-').join(' · ')}
                    </span>}
                    qty={s.quantity}
                  />
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </SectionCard>
  );
};
